import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Container,
  Grid,
  Card,
  CardContent,
  useTheme,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PeopleIcon from '@mui/icons-material/People';
import InventoryIcon from '@mui/icons-material/Inventory';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { ROUTES, APP_NAME } from '../../config/app.config';
import { useAuth } from '../../context/AuthContext';
import styles from './HomePage.module.css';

export function HomePage() {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  
  // Auto-redirect sellers (shop_manager) to stock page
  useEffect(() => {
    if (isAuthenticated && user) {
      const userRole = user?.role?.toUpperCase?.() || user?.role || '';
      const userRoles = user?.roles || [];
      const roleArray = Array.isArray(userRoles) ? userRoles.map(r => String(r).toUpperCase()) : [];
      
      // Check if user is seller/manager (shop_manager) - they should go to stock
      const isSeller = userRole === 'SHOP_MANAGER' || 
                      userRole === 'MANAGER' ||
                      roleArray.includes('SHOP_MANAGER') ||
                      roleArray.includes('MANAGER');
      
      if (isSeller) {
        navigate(ROUTES.STOCK, { replace: true });
      }
    }
  }, [isAuthenticated, user, navigate]);

  const features = [
    {
      icon: DashboardIcon,
      title: t('nav.dashboard'),
      description: 'Monitor your business performance with real-time analytics and insights',
      color: theme.palette.primary.main,
    },
    {
      icon: ShoppingCartIcon,
      title: t('nav.orders'),
      description: 'Manage orders efficiently with comprehensive order tracking and processing',
      color: theme.palette.success.main,
    },
    {
      icon: PeopleIcon,
      title: t('nav.clients'),
      description: 'Keep track of your customers and their information in one place',
      color: theme.palette.info.main,
    },
    {
      icon: InventoryIcon,
      title: t('nav.products'),
      description: 'Organize and manage your product catalog with ease',
      color: theme.palette.warning.main,
    },
  ];

  return (
    <Box className={styles.container}>
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white',
          py: { xs: 6, md: 10 },
          mb: 6,
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', maxWidth: 800, mx: 'auto' }}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 700,
                mb: 2,
                fontSize: { xs: '2rem', md: '3rem' },
              }}
            >
              {isAuthenticated
                ? `Welcome back, ${user?.username || user?.email || 'User'}!`
                : `Welcome to ${APP_NAME}`}
            </Typography>
            <Typography
              variant="h6"
              sx={{
                opacity: 0.9,
                mb: 4,
                fontSize: { xs: '1rem', md: '1.25rem' },
                fontWeight: 400,
              }}
            >
              {isAuthenticated
                ? 'Manage your business operations efficiently with our comprehensive ERP system'
                : t('home.subtitle') || 'Streamline your business operations with our powerful ERP solution'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              {isAuthenticated ? (() => {
                // Determine default route based on user role
                // Sellers (shop_manager) should go to stock page, admins to dashboard
                const userRole = user?.role?.toUpperCase?.() || user?.role || '';
                const userRoles = user?.roles || [];
                const roleArray = Array.isArray(userRoles) ? userRoles.map(r => String(r).toUpperCase()) : [];
                
                // Check if user is seller/manager (shop_manager)
                const isSeller = userRole === 'SHOP_MANAGER' || 
                                userRole === 'MANAGER' ||
                                roleArray.includes('SHOP_MANAGER') ||
                                roleArray.includes('MANAGER');
                
                const defaultRoute = isSeller ? ROUTES.STOCK : ROUTES.DASHBOARD;
                const buttonLabel = isSeller ? t('nav.stock') : t('nav.dashboard');
                
                return (
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => navigate(defaultRoute)}
                    endIcon={<ArrowForwardIcon />}
                    sx={{
                      bgcolor: 'white',
                      color: theme.palette.primary.main,
                      px: 4,
                      py: 1.5,
                      fontSize: '1rem',
                      fontWeight: 600,
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 0.9)',
                      },
                    }}
                  >
                    {buttonLabel}
                  </Button>
                );
              })() : (
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate(ROUTES.LOGIN)}
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    bgcolor: 'white',
                    color: theme.palette.primary.main,
                    px: 4,
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 600,
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.9)',
                    },
                  }}
                >
                  {t('nav.login')}
                </Button>
              )}
            </Box>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg">
        <Box sx={{ mb: 6, textAlign: 'center' }}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            Powerful Features
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
            Everything you need to manage your business operations efficiently
          </Typography>
        </Box>

        <Grid container spacing={3} sx={{ mb: 6 }}>
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0px 20px 25px -5px rgba(0, 0, 0, 0.1), 0px 10px 10px -5px rgba(0, 0, 0, 0.04)',
                    },
                  }}
                >
                  <CardContent sx={{ p: 3, textAlign: 'center' }}>
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: 2,
                        backgroundColor: `${feature.color}15`,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2,
                      }}
                    >
                      <Icon sx={{ fontSize: 32, color: feature.color }} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Container>
    </Box>
  );
}
